// src/pages/LeadershipPage.jsx
import { useEffect, useState } from "react";
import { getLeaders } from "../api/LeadershipApis";
import Footer from "../components/common/Footer";
import "../static/LeadershipPage.css";

export default function LeadershipPage() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getLeaders()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.results ?? res.data ?? [];
        setLeaders(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load leadership team.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="ldr-page">
      <div className="ldr-container">
        <header className="ldr-header">
          <div className="ldr-header-content">
            <span className="ldr-header-icon" aria-hidden="true">
              <i className="fa-solid fa-users" />
            </span>
            <h1 className="ldr-title">Leadership</h1>
            <p className="ldr-subtitle">
              The people building Hayducate are founders, co-founders, and
              directors who guide our mission to empower learners across
              Africa.
            </p>
          </div>
        </header>

        {loading && (
          <div className="ldr-grid" aria-busy="true" aria-live="polite">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ldr-skel-card" aria-hidden="true">
                <div className="ldr-skel-avatar" />
                <div className="ldr-skel-line ldr-skel-line-name" />
                <div className="ldr-skel-line ldr-skel-line-role" />
                <div className="ldr-skel-line ldr-skel-line-quote" />
                <div className="ldr-skel-line ldr-skel-line-quote short" />
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="ldr-error">
            <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
            <h2>Something went wrong</h2>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && leaders.length === 0 && (
          <div className="ldr-empty">
            <i className="fa-solid fa-user-group" aria-hidden="true" />
            <h2>Coming soon</h2>
            <p>We'll introduce our leadership team here shortly.</p>
          </div>
        )}

        {!loading && !error && leaders.length > 0 && (
          <div className="ldr-grid">
            {leaders.map((leader) => (
              <LeaderCard key={leader.id} leader={leader} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

/* -----------------------------------------------------------------
   Individual leader card
   Structure:
   <article .ldr-card>
     <div .ldr-photo>           ← square image or placeholder
     <span .ldr-role>           ← absolute-positioned badge over photo
     <div .ldr-card-body>       ← everything else
   </article>
----------------------------------------------------------------- */
function LeaderCard({ leader }) {
  const qualifications = (leader.qualifications || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <article className="ldr-card">
      {/* Photo (square) */}
      <div className="ldr-photo">
        {leader.photo ? (
          <img src={leader.photo} alt={leader.name} loading="lazy" />
        ) : (
          <div className="ldr-photo-placeholder" aria-hidden="true">
            <i className="fa-solid fa-user" />
          </div>
        )}
      </div>

      {/* Role badge — floats over the photo */}
      <span className={`ldr-role ldr-role-${leader.role}`}>
        {leader.role_display || leader.role}
      </span>

      {/* Body */}
      <div className="ldr-card-body">
        <h2 className="ldr-name">{leader.name}</h2>

        {leader.title && <p className="ldr-role-title">{leader.title}</p>}

        {qualifications.length > 0 && (
          <ul className="ldr-quals">
            {qualifications.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        )}

        {leader.quote && (
          <blockquote className="ldr-quote">
            <i
              className="fa-solid fa-quote-left ldr-quote-mark"
              aria-hidden="true"
            />
            <p>{leader.quote}</p>
          </blockquote>
        )}

        {(leader.linkedin || leader.email) && (
          <div className="ldr-links">
            {leader.linkedin && (
              <a
                href={leader.linkedin}
                className="ldr-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${leader.name} on LinkedIn`}
              >
                <i className="fa-brands fa-linkedin-in" aria-hidden="true" />
              </a>
            )}
            {leader.email && (
              <a
                href={`mailto:${leader.email}`}
                className="ldr-link"
                aria-label={`Email ${leader.name}`}
              >
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}